import 'bootstrap/js/dist/carousel';
import * as money from 'shop/assets/js/utils/money';
import intersect from '@alpinejs/intersect';
import GLightbox from 'glightbox';
import Product from 'shop/assets/js/entities/Product';
import ProductVariant from '../../../js/entities/ProductVariant';
import CustomerEmailCapturedEvent from '../../../js/events/customer-email-captured-event';

/**
 * @typedef {Object} CrossSellVariant
 *
 * @property {number} quantity Quantity to be added to cart (use zero to match 'main product' quantity)
 * @property {ProductVariant} variant
 */

GLightbox({ selector: '.glightbox' });
window.Alpine.plugin(intersect);

document.addEventListener('DOMContentLoaded', () => {
    window.addEventListener('scroll', () => {
        let reviewsElement = document.getElementById('product-reviews');
        let scrollTopButton = document.getElementById('scroll-top-button');

        if (reviewsElement.getBoundingClientRect().y < window.innerHeight) {
            scrollTopButton.style.transform = 'none';
        } else {
            scrollTopButton.style.transform = 'translateY(152px)';
        }
    });
});

document.addEventListener('alpine:init', () => {
    window.Alpine.data('ProductShow', () => ({
        product: null,
        variant: null,
        /**
         * The keys (first level) of this object are cross-sell provider codes, ex: 'pdp_turbo', 'pdp_gift_wrapper'
         * The keys on the second level are variant codes
         * The inner items are ProductVariants)
         * @type {Object.<string, Object.<string, ProductVariant>>}
         */
        crossSellSelectionByProviderCode: {},
        quantity: null,
        total: null,
        loading: null,
        error: null,
        init() {
            this.product = new Product(JSON.parse(this.$el.getAttribute('data-product')));

            if (window.location.href.indexOf('#') !== -1) {
                this.loadVariantFromUrlAnchor(this.$el.getAttribute('data-variant-code'));
            } else {
                this.loadVariant(this.$el.getAttribute('data-variant-code'));
            }
        },
        loadVariantFromUrlAnchor(fallbackVariantCode) {
            const url = window.location.href;
            const urlOptionValues = url.substring(url.indexOf('#') + 1).split(',');
            let urlVariantCode = null;

            urlOptionValues.forEach(function (value) {
                if (/^(PCK)?\d{8}$/.test(value)) {
                    urlVariantCode = value;
                }
            });

            if (urlVariantCode) {
                this.loadVariant(urlVariantCode);

                return;
            }

            // Primero buscamos una variante sellable para evitar que "findVariantByOptions"
            // nos devuelva una variante equivalente deshabilitada.
            let variant = this.product.findSellableVariantByOptions(urlOptionValues);
            if (!variant) {
                variant = this.product.findVariantByOptions(urlOptionValues);
            }

            if (variant) {
                this.loadVariant(variant.code);
            } else {
                this.loadVariant(fallbackVariantCode);
            }
        },
        loadVariant(variantCode) {
            this.variant = this.product.findVariantByCode(variantCode);
            this.quantity = this.getInitialQuantity();
            this.loadVariantImages();
            this.updateUrlAnchor();
            this.updateTotal();
            this.thumbnailsNavigationVisibility(true);

            document.dispatchEvent(
                new CustomEvent('ProductShow_Variant_PostLoad', {
                    detail: {
                        variant: this.variant,
                    },
                })
            );
        },
        getInitialQuantity() {
            if (!this.variant.isSellable) {
                return 0;
            }

            if (this.variant.minimumOrderQuantity) {
                return this.variant.minimumOrderQuantity;
            }

            return 1;
        },
        updateUrlAnchor() {
            if (this.variant.optionValues.length > 0) {
                const baseUrl = location.protocol + '//' + location.host + location.pathname + location.search;
                const newURL = `${baseUrl}#${this.variant.optionValues.join(',')}`;
                const newTitle = null;
                const newState = {};
                window.history.replaceState(newState, newTitle, newURL);
            }
        },
        loadVariantImages() {
            document.querySelectorAll(`#variant-images-${this.variant.code} .carousel-wrapper img`).forEach((img) => {
                img.removeAttribute('loading');
            });
        },
        increaseQuantity() {
            this.quantity += 1;
            this.updateTotal();

            document.dispatchEvent(
                new CustomEvent('ProductShow_IncreaseQuantity_Click', {
                    detail: {
                        variant: this.variant,
                        quantity: this.quantity,
                    },
                })
            );
        },
        decreaseQuantity() {
            this.quantity -= 1;
            this.updateTotal();
        },
        selectQuantity(quantity) {
            this.quantity = quantity;
            this.updateTotal();
        },
        isSelectedOnCrossSellForProvider(providerCode, variantCode) {
            return (
                typeof this.crossSellSelectionByProviderCode[providerCode] !== 'undefined' &&
                typeof this.crossSellSelectionByProviderCode[providerCode][variantCode] !== 'undefined'
            );
        },
        isSelectedOnCrossSellOnAnyProvider(variantCode) {
            let found = false;
            for (const providerCode of Object.keys(this.crossSellSelectionByProviderCode)) {
                if (typeof this.crossSellSelectionByProviderCode[providerCode][variantCode] !== 'undefined') {
                    found = true;
                    break;
                }
            }
            return found;
        },
        getSelectedCrossSellVariant(providerCode, variantCode) {
            if (!this.isSelectedOnCrossSellForProvider(providerCode, variantCode)) {
                return null;
            }
            return this.crossSellSelectionByProviderCode[providerCode][variantCode];
        },
        toggleSelectedOnCrossSell(providerCode, multipleSelectionAllowed) {
            let variant = new ProductVariant(JSON.parse(this.$el.getAttribute('data-variant')));
            if (this.isSelectedOnCrossSellForProvider(providerCode, variant.code)) {
                delete this.crossSellSelectionByProviderCode[providerCode][variant.code];
            } else {
                if (!multipleSelectionAllowed || typeof this.crossSellSelectionByProviderCode[providerCode] === 'undefined') {
                    // Delete all previously selected items of the same type
                    this.crossSellSelectionByProviderCode[providerCode] = {};
                }

                this.crossSellSelectionByProviderCode[providerCode][variant.code] = variant;
            }
            this.updateTotal();
        },
        updateTotal() {
            this.total = money.format(this.getTotal());
        },
        getTotal() {
            let crossSellTotal = 0;
            Object.keys(this.crossSellSelectionByProviderCode).forEach((providerCode) => {
                Object.keys(this.crossSellSelectionByProviderCode[providerCode]).forEach((variantCode) => {
                    crossSellTotal += this.quantity * this.crossSellSelectionByProviderCode[providerCode][variantCode].price;
                });
            });
            return this.quantity * this.variant.price + crossSellTotal;
        },
        get crossSellProviderHasSelection() {
            return (code) => {
                const provider = this.crossSellSelectionByProviderCode?.[code];
                return provider ? Object.keys(provider).length > 0 : false;
            };
        },
        getCrossSellVariantCodes() {
            let variantCodes = [];
            Object.keys(this.crossSellSelectionByProviderCode).forEach((providerCode) => {
                Object.keys(this.crossSellSelectionByProviderCode[providerCode]).forEach((variantCode) => {
                    variantCodes.push(variantCode);
                });
            });
            return variantCodes;
        },
        getCrossSellSelectionAsFormData() {
            let crossSellSelectionAsFormData = [];
            Object.keys(this.crossSellSelectionByProviderCode).forEach((providerCode) => {
                Object.keys(this.crossSellSelectionByProviderCode[providerCode]).forEach((variantCode) => {
                    crossSellSelectionAsFormData.push({
                        providerCode,
                        variantCode,
                    });
                });
            });
            return crossSellSelectionAsFormData;
        },
        addToCart() {
            this.error = null;
            this.loading = true;

            const url = this.$el.getAttribute('data-add-to-cart-url');
            const formData = this.getFormData();
            const cartModalContainer = document.getElementById('cart-modal-container');
            const cartWidgetContainer = document.getElementById('cart-widget-container');
            const addToCartModalUrl = cartModalContainer.dataset.addToCartModalUrl;
            const cartWidgetUrl = cartModalContainer.dataset.cartWidgetUrl;

            document.dispatchEvent(
                new CustomEvent('ProductShow_AddToCart_Click', {
                    detail: {
                        variant: this.variant,
                        crossSellSelectionByProviderCode: this.crossSellSelectionByProviderCode,
                        quantity: this.quantity,
                        total: this.getTotal(),
                    },
                })
            );

            $.post(url, formData)
                .then(() => {
                    let modalUrl = new URL(addToCartModalUrl);
                    let variantCodes = [this.variant.code, ...this.getCrossSellVariantCodes()];

                    modalUrl.search = new URLSearchParams({
                        variantCodes: variantCodes,
                        quantity: this.quantity,
                    }).toString();

                    this.getView(modalUrl).then((response) => {
                        if (response) {
                            cartModalContainer.innerHTML = response;

                            if (this.isDesktop()) {
                                const modal = document.getElementById('modal-cart');

                                modal.addEventListener('shown.bs.modal', (event) => {
                                    this.initiateOpacityTransition(event.target);
                                });

                                new window.bootstrap.Modal(modal).show();
                            } else {
                                const offcanvas = document.getElementById('offcanvas-cart');

                                offcanvas.addEventListener('shown.bs.offcanvas', (event) => {
                                    this.initiateOpacityTransition(event.target);
                                });

                                new window.bootstrap.Offcanvas(offcanvas).show();
                            }

                            document.dispatchEvent(
                                new CustomEvent('CrossSellModal_Show', {
                                    detail: {
                                        variant: this.variant,
                                    },
                                })
                            );
                        }
                    });

                    let widgetUrl = new URL(cartWidgetUrl);
                    widgetUrl.search = new URLSearchParams({ template: 'widget' }).toString();

                    this.getView(widgetUrl).then((response) => {
                        if (response) {
                            cartWidgetContainer.innerHTML = response;
                        }
                    });
                })
                .catch((error) => {
                    document.dispatchEvent(new CustomEvent('ProductShow_AddToCart_Error'));

                    const errorMessage = this.extractErrorMessage(error);

                    this.error = errorMessage;

                    this.logError(formData, error, errorMessage);

                    this.$refs.productTitle.scrollIntoView({ behavior: 'smooth' });
                })
                .always(() => {
                    setTimeout(() => {
                        this.loading = false;
                    }, 1000);
                });
        },
        initiateOpacityTransition(targetElement) {
            targetElement.querySelectorAll('.transition-opacity').forEach((element) => {
                element.classList.remove('opacity-0');
            });
        },
        getFormData() {
            const formData = {
                sylius_add_to_cart: {
                    cartItem: {
                        quantity: this.quantity,
                    },
                },
            };

            if (this.product.type === 'configurable') {
                formData.sylius_add_to_cart.cartItem.variant = this.variant.code;
            }

            formData.crossSellSelectionByProviderCode = this.getCrossSellSelectionAsFormData();

            return formData;
        },
        extractErrorMessage: function (error) {
            if (error?.responseJSON?.errors?.errors?.length > 0) {
                return error.responseJSON.errors.errors.shift();
            } else if (error instanceof TypeError) {
                return error.message;
            } else {
                return 'Lo sentimos, ha ocurrido un error. Por favor, inténtelo de nuevo.';
            }
        },
        logError: function (formData, error, errorMessage) {
            console.error('[AddToCartError]', {
                payload: JSON.stringify(formData, null, 4),
                response: JSON.stringify(error?.response, null, 4),
                data: JSON.stringify(error?.response?.data, null, 4),
                error: JSON.stringify(error, null, 4),
                error_message: errorMessage,
            });
        },
        isMobileDevice() {
            return window.matchMedia('(max-width: 992px)').matches;
        },
        sendStockNotification() {
            this.stockNotificationLoading = true;
            this.stockNotificationSuccess = false;
            this.stockNotificationError = false;

            document.dispatchEvent(new CustomerEmailCapturedEvent(this.email));

            fetch(this.stockNotificationUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({
                    product_id: this.product.data.id,
                    email: this.email,
                }),
            })
                .then((response) => {
                    if (response.ok) {
                        this.stockNotificationSuccess = true;

                        document.dispatchEvent(
                            new CustomEvent('ProductShow_StockNotificationForm_Submit', {
                                detail: {
                                    price: this.variant.price / 100,
                                },
                            })
                        );
                    } else {
                        this.stockNotificationError = true;
                    }
                })
                .catch((error) => {
                    console.error('Request failed:', error);
                })
                .finally(() => {
                    this.stockNotificationLoading = false;
                });
        },
        thumbnailsNavigationVisibility(waitForImagesLoaded = false) {
            const wrapperElement = document.getElementById('variant-thumbnails-' + this.variant.code);
            const thumbnailsElement = wrapperElement.querySelector('.carousel-thumbnails');
            const prevButton = wrapperElement.querySelector('.prev-button');
            const nextButton = wrapperElement.querySelector('.next-button');
            const images = [...wrapperElement.querySelectorAll('img')];

            if (waitForImagesLoaded) {
                Promise.all(images.map((image) => new Promise((resolve) => (image.onload = resolve)))).then(() => {
                    this.handleThumbnailsPrevButtonVisibility(thumbnailsElement, prevButton);
                    this.handleThumbnailsNextButtonVisibility(thumbnailsElement, nextButton);
                });
            } else {
                this.handleThumbnailsPrevButtonVisibility(thumbnailsElement, prevButton);
                this.handleThumbnailsNextButtonVisibility(thumbnailsElement, nextButton);
            }
        },
        handleThumbnailsPrevButtonVisibility(thumbnailsElement, prevButton) {
            if (thumbnailsElement.scrollTop === 0) {
                prevButton.classList.add('invisible');
            } else {
                prevButton.classList.remove('invisible');
            }
        },
        handleThumbnailsNextButtonVisibility(thumbnailsElement, nextButton) {
            if (
                (thumbnailsElement.scrollTop !== 0 &&
                    thumbnailsElement.offsetHeight + thumbnailsElement.scrollTop >= thumbnailsElement.scrollHeight - 1) ||
                thumbnailsElement.scrollHeight <= thumbnailsElement.offsetHeight
            ) {
                nextButton.classList.add('invisible');
            } else {
                nextButton.classList.remove('invisible');
            }
        },
        isDesktop() {
            return window.matchMedia('screen and (min-width:992px)').matches;
        },
        getView(url) {
            return fetch(url, {
                method: 'GET',
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    return response.text();
                })
                .catch((error) => {
                    console.error('Request failed:', error);
                });
        },
    }));
});
